import React, { Component } from 'react'
import { injectIntl } from 'react-intl'
import messages from './messages'

import ReactHighmaps from 'react-highcharts/ReactHighmaps.src'

import MapChartConfig from './MapChartConfig'
import MapChartAdapter from './MapChartAdapter'

import API from 'utils/api/api'
import APIURL from 'utils/api/urls'
import AppTools from 'utils/app/appTools'
import QueryTools from 'utils/queries/queryTools'
import ObjectTools from 'utils/objects/objectTools'
import Colors from 'utils/colors/colors'

import Loading from 'components/Loading'
import Error from 'components/Error'

class MapChart extends Component {
	constructor(props) {
		super(props)

		const {
			intl: { formatMessage, formatNumber }
		} = props
		this.formatNumber = formatNumber
		this.formatMessage = formatMessage
		AppTools.importFormatter(formatMessage)

		this.state = {
			loading: true,
			error: false
		}
		this.mapChart = React.createRef()
	}

	componentDidMount() {
		this.props.fn(this.mapChart)
		const generalRegionsData = JSON.parse(this.props.generalRegionsData)
		const { regionsData, referenceRegionsData, sameCountryRegionsData, comparative } = generalRegionsData

		this.setState(
			{
				regionsData,
				referenceRegionsData,
				sameCountryRegionsData,
				comparative
			},
			() => this.getMapChart()
		)
	}

	shouldComponentUpdate(nextProps, nextState) {
		const shouldUpdate = !ObjectTools.compareObjects(this.props, nextProps) || !ObjectTools.compareObjects(this.state, nextState)
		return shouldUpdate
	}

	componentDidUpdate(prevProps) {
		if (!ObjectTools.compareObjects(this.props, prevProps)) {
			const generalRegionsData = JSON.parse(this.props.generalRegionsData)
			const { regionsData, referenceRegionsData, sameCountryRegionsData, comparative } = generalRegionsData
			this.setState(
				{
					regionsData,
					referenceRegionsData,
					sameCountryRegionsData,
					comparative,
					loading: true,
					error: false
				},
				() => this.getMapChart()
			)
		}
		this.setZoomAfterRendering()
	}

	getMapChart = ({ indicator, selectedRegionId } = { indicator: this.props.indicator, selectedRegionId: this.props.selectedRegionId }) => {
		let regionsData = this.state.regionsData
		let referenceRegionsData = this.state.referenceRegionsData
		let sameCountryRegionsData = this.state.sameCountryRegionsData
		let comparative = this.state.comparative

		const regionsToShow = JSON.parse(this.props.regionsToShow)
		const { allRegionsFilter, referenceRegionsFilter, sameCountryRegionsFilter, otherRegionsFilter } = regionsToShow
		let comparativeShouldShow = Object.keys(otherRegionsFilter).length !== 0

		let chartConfig = this.selectConfig('general').chartConfig
		let year = this.props.year
		let promises = []

		let query = QueryTools.formatQuerySingleGEOFromId(selectedRegionId)
		promises.push(Promise.resolve(API.getQuery(APIURL.getDataInSpecificYearForIndicatorAndRegions({ indicator, query, year }))))

		if (allRegionsFilter) {
			let query = QueryTools.formatQueryInDifferentGEOs(regionsData)
			promises.push(Promise.resolve(API.getQuery(APIURL.getDataInSpecificYearForIndicatorAndRegions({ indicator, query, year }))))
		}

		if (referenceRegionsFilter) {
			let query = QueryTools.formatQueryInDifferentGEOs(referenceRegionsData)
			promises.push(Promise.resolve(API.getQuery(APIURL.getDataInSpecificYearForIndicatorAndRegions({ indicator, query, year }))))
		}

		if (sameCountryRegionsFilter) {
			let query = QueryTools.formatQueryInDifferentGEOs(sameCountryRegionsData)
			promises.push(Promise.resolve(API.getQuery(APIURL.getDataInSpecificYearForIndicatorAndRegions({ indicator, query, year }))))
		}

		if (comparativeShouldShow) {
			let query = ''
			let offset = 0
			comparative.forEach(comparativeItem => {
				query += '&' + QueryTools.formatQueryInDifferentGEOs(comparativeItem.regionsData, offset)
				offset = offset + comparativeItem.regionsData.length
			})
			promises.push(Promise.resolve(API.getQuery(APIURL.getDataInSpecificYearForIndicatorAndRegions({ indicator, query, year }))))
		}

		Promise.all(promises)
			.then(values => MapChartAdapter.preFormatDataFromResponse(...values))
			.then(data => MapChartAdapter.formatMapSeries({ regionsData, data, selectedRegionId }))
			.then(series => {
				let customMapConfig = ObjectTools.copyObject(chartConfig)
				let { max, min } = MapChartAdapter.getMaxAndMinValue(series)

				if (this.props.expanded) {
					let panelHeight = document.querySelector('.panel-expanded').offsetHeight
					let panelHeaderHeight = document.querySelector('.panel-expanded>.panel-heading').offsetHeight
					let panelFooterHeight = document.querySelector('.panel-expanded>.panel-footer').offsetHeight
					let panelBody = document.querySelector('.panel-expanded>.panel-body')
					let paddingTop = parseInt(window.getComputedStyle(panelBody).getPropertyValue('padding-top'), 10)
					let paddingBottom = parseInt(window.getComputedStyle(panelBody).getPropertyValue('padding-bottom'), 10)
					let panelBodyHeight = panelHeight - panelHeaderHeight - panelFooterHeight - paddingTop - paddingBottom
					customMapConfig.chart.height = `${panelBodyHeight}px`
				}

				customMapConfig.legend.enabled = this.props.showLegend
				customMapConfig.credits.text = AppTools.getComposedDataSource(this.props.indicatorData.dataSource)

				let divergent = false;
				let min_sign = Math.sign(min);
				let max_sign = Math.sign(max);
				if ( (min_sign !== 0) & (max_sign !== 0) & (min_sign !== max_sign) ) {
					divergent = true;
					let max_abs = Math.max( Math.abs( min ), Math.abs( max ) );
					min = -max_abs;
					max = max_abs;
				}

				// customMapConfig.colorAxis.max = max
				// customMapConfig.colorAxis.min = min
				let mapMaxColor
				let mapMinColor
				let chartTitle
				if (this.props.indicatorData.rankOrdering === 'descending') {
					if (this.props.gender === 'Female') {
						mapMaxColor = Colors.colorToRGBA(Colors.mapMaxColorDescendingFemale)
						mapMinColor = Colors.colorToRGBA(Colors.mapMinColorDescendingFemale)
						chartTitle = `${this.formatMessage(messages.female)} | ${this.formatMessage(messages.yearAndUnit, {
							year: year,
							unit: this.props.indicatorData.unit
						})}`
					} else if (this.props.gender === 'Male') {
						mapMaxColor = Colors.colorToRGBA(Colors.mapMaxColorDescending)
						mapMinColor = Colors.colorToRGBA(Colors.mapMinColorDescending)
						chartTitle = `${this.formatMessage(messages.male)} | ${this.formatMessage(messages.yearAndUnit, {
							year: year,
							unit: this.props.indicatorData.unit
						})}`
					} else {
						mapMaxColor = Colors.colorToRGBA(Colors.mapMaxColorDescending)
						mapMinColor = Colors.colorToRGBA(Colors.mapMinColorDescending)
						chartTitle = this.formatMessage(messages.yearAndUnit, { year: year, unit: this.props.indicatorData.unit })
					}
				} else {
					if (this.props.gender === 'Female') {
						mapMaxColor = Colors.colorToRGBA(Colors.mapMaxColorAscendingFemale)
						mapMinColor = Colors.colorToRGBA(Colors.mapMinColorAscendingFemale)
						chartTitle = `${this.formatMessage(messages.female)} | ${this.formatMessage(messages.yearAndUnit, {
							year: year,
							unit: this.props.indicatorData.unit
						})}`
					} else if (this.props.gender === 'Male') {
						mapMaxColor = Colors.colorToRGBA(Colors.mapMaxColorAscending)
						mapMinColor = Colors.colorToRGBA(Colors.mapMinColorAscending)
						chartTitle = `${this.formatMessage(messages.male)} | ${this.formatMessage(messages.yearAndUnit, {
							year: year,
							unit: this.props.indicatorData.unit
						})}`
					} else {
						mapMaxColor = Colors.colorToRGBA(Colors.mapMaxColorAscending)
						mapMinColor = Colors.colorToRGBA(Colors.mapMinColorAscending)
						chartTitle = this.formatMessage(messages.yearAndUnit, { year: year, unit: this.props.indicatorData.unit })
					}
				}

				//   customMapConfig.colorAxis.maxColor = mapMaxColor
				//   customMapConfig.colorAxis.minColor = mapMinColor
				if ( divergent ) {
					if (this.props.gender === 'Female') {
						mapMinColor = Colors.colorToRGBA( Colors.mapMaxColorAscendingFemale );
						mapMaxColor = Colors.colorToRGBA( Colors.mapMaxColorDescendingFemale );
					} else {
						mapMinColor = Colors.colorToRGBA( Colors.mapMaxColorAscending );
						mapMaxColor = Colors.colorToRGBA( Colors.mapMaxColorDescending );
					}
					customMapConfig.colorAxis.stops = [
						[ 0.0, mapMinColor ],
						[ 0.5, "rgba( 255, 255, 255, 1 )" ],
						[ 1.0, mapMaxColor ]
					];
					customMapConfig.colorAxis.min = min;
					customMapConfig.colorAxis.max = max;
				} else {
					customMapConfig.colorAxis.stops = [
						[ 0.0, mapMinColor ],
						[ 1.0, mapMaxColor ]
					];
				}
				customMapConfig.title.text = chartTitle

				customMapConfig.series = series
				const formatNumber = this.formatNumber
				const unit = this.props.indicatorData.unit
				customMapConfig.tooltip.headerFormat = '<span style="font-size: 10px">' + year + '</span><br/>'
				customMapConfig.tooltip.pointFormatter = function () {
					return '<span style="color:' + this.color + '">●</span><b>' + this.regionName + '</b>: ' + formatNumber(this.value.toFixed(2)) + ' ' + unit
				}

				this.setState({ chartConfig: customMapConfig, loading: false })
			})
			.catch(reason => {
				console.log(reason);
				this.setState({ error: true, loading: false })
			})
	}

	selectConfig = config => {
		switch (config) {
			case 'selectedRegion':
				return {
					chartConfig: MapChartConfig.selectedRegionConfig
				}
			case 'general':
				return {
					chartConfig: MapChartConfig.generalConfig
				}
			default:
				return {
					chartConfig: MapChartConfig.generalConfig
				}
		}
	}

	getZoomBeforeRendering = () => {
		if (this.mapChart.current) {
			let chartArray = this.mapChart.current.Highcharts.charts
			this.zoomXMin = chartArray[chartArray.length - 1].xAxis[0].getExtremes().min
			this.zoomXMax = chartArray[chartArray.length - 1].xAxis[0].getExtremes().max
			this.zoomYMin = chartArray[chartArray.length - 1].yAxis[0].getExtremes().min
			this.zoomYMax = chartArray[chartArray.length - 1].yAxis[0].getExtremes().max
		}
	}

	setZoomAfterRendering = () => {
		if (this.mapChart.current) {
			let chartArray = this.mapChart.current.Highcharts.charts
			chartArray[chartArray.length - 1].xAxis[0].setExtremes(this.zoomXMin, this.zoomXMax, false)
			chartArray[chartArray.length - 1].yAxis[0].setExtremes(this.zoomYMin, this.zoomYMax, false)
		}
	}

	renderIfAllOK = () => {
		let renderingComponent = null
		if (this.state.error) {
			renderingComponent = <Error />
		} else if (this.state.loading) {
			renderingComponent = <Loading />
		} else {
			this.getZoomBeforeRendering()
			renderingComponent = <ReactHighmaps ref={this.mapChart} config={this.state.chartConfig} />
		}
		return renderingComponent
	}

	render() {
		return <React.Fragment>{this.renderIfAllOK()}</React.Fragment>
	}
}

export default injectIntl(MapChart)



// WEBPACK FOOTER //
// ./src/modules/Charts/Map/MapChart/MapChart.jsx